import { useQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { useRefetchableQuery } from "core/hooks/useRefetchableQuery";
import {
  GetTask as GetTaskGenerated,
  GetTaskVariables as GetTaskVariablesGenerated,
  GetTask_task_booking_Inquiry as TaskBookingInquiryGenerated,
  GetTask_task_checklistItems as TaskChecklistItemsGenerated,
  GetTask_task_cleaningRequest_company as TaskCleaningRequestCompanyGenerated,
  GetTask_task_cufSpace as TaskDataCufSpaceGenerated,
  GetTask_task as TaskDataGenerated,
  GetTask_task_tomTaskRecurrenceSetting as TaskRecurrenceSettingGenerated,
  GetTask_task_unit_bookingInfo_currentBooking as TaskUnitBookingInfoCurrentBookingGenerated,
  GetTask_task_unit_bookingInfo as TaskUnitBookingInfoGenerated,
  GetTask_task_unit_bookingInfo_previousBooking as TaskUnitBookingInfoPreviousBookingGenerated,
  GetTask_task_unit_bookingInfo_upcomingBooking as TaskUnitBookingInfoUpcomingBookingGenerated,
  GetTask_task_unit as TaskUnitGenerated,
} from "core/queries/__generated__/GetTask";

export enum TaskSubjectType {
  Building = "Building",
  City = "City",
  CleaningRequest = "Hk::CleaningRequest",
  Inquiry = "Inquiry",
  InternalHold = "InternalHold",
  LeaseInquiry = "LeaseInquiry",
  Space = "Cuf::Space",
  Unit = "Unit",
}

export type TaskSubjectTypes =
  | TaskSubjectType.Building
  | TaskSubjectType.City
  | TaskSubjectType.CleaningRequest
  | TaskSubjectType.Inquiry
  | TaskSubjectType.InternalHold
  | TaskSubjectType.LeaseInquiry
  | TaskSubjectType.Space
  | TaskSubjectType.Unit;

// Queries
export interface TaskData extends TaskDataGenerated {}
export interface GetTaskResponse extends GetTaskGenerated {}
export interface GetTaskVariables extends GetTaskVariablesGenerated {}
export interface TaskCufSpace extends TaskDataCufSpaceGenerated {}
export interface TaskCleaningRequestCompany
  extends TaskCleaningRequestCompanyGenerated {}
export interface TaskBookingInquiry extends TaskBookingInquiryGenerated {}
export interface TaskChecklistItem extends TaskChecklistItemsGenerated {}
export interface TaskRecurrenceSetting extends TaskRecurrenceSettingGenerated {}
export interface TaskUnit extends TaskUnitGenerated {}
export interface TaskUnitBookingInfo extends TaskUnitBookingInfoGenerated {}
export interface TaskUnitBookingInfoPreviousBooking
  extends TaskUnitBookingInfoPreviousBookingGenerated {}
export interface TaskUnitBookingInfoCurrentBooking
  extends TaskUnitBookingInfoCurrentBookingGenerated {}
export interface TaskUnitBookingInfoUpcomingBooking
  extends TaskUnitBookingInfoUpcomingBookingGenerated {}
export const getTask = gql`
  query GetTask($taskId: ID!) {
    task(id: $taskId) {
      id
      type
      name
      asanaUrl
      assignee {
        id
        firstName
        lastName
        email
      }
      assignees {
        id
        firstName
        lastName
        email
      }
      author {
        avatar: profilePicture {
          id
          url
        }
        id
        name
      }
      attachments {
        createdAt
        id
        attachableId
        attachableType
        resourceType
        url
        user {
          avatar: profilePicture {
            id
            url
          }
          id
          name
        }
      }
      booking {
        id
        account {
          id
          firstName
          lastName
          email
          primaryPhoneNumber
          salesforceAssociation {
            id
            name
            salesforceUrl
          }
        }
        bookingUrl
        checkInTime
        checkOutTime
        ... on Inquiry {
          inquiryId: id
        }
        ... on LeaseInquiry {
          leaseInquiryId: id
        }
      }
      checklistItems {
        id
        createdAt
        name
        required
        status
      }
      city {
        id
      }
      cufSpace {
        id
        building {
          id
          internalRef
          name
        }
        defaultCleaningMinutes
        internalRef
        type
      }
      dueAt
      duringStay
      estimatedDuration
      priority
      unit {
        id
        address
        bookingInfo {
          previousBooking {
            id
            account {
              id
              firstName
              lastName
            }
            checkInTime
            checkOutTime
          }
          currentBooking {
            id
            account {
              id
              firstName
              lastName
            }
            checkInTime
            checkOutTime
          }
          upcomingBooking {
            id
            account {
              id
              firstName
              lastName
            }
            checkInTime
            checkOutTime
          }
        }
        beds {
          id
          bedSize
        }
        checkInCheckOutInfo {
          isVacantAllDayToday
          checkInToday {
            id
            checkInTime
            checkOutTime
            account {
              id
            }
          }
          checkOutToday {
            id
            checkOutTime
            account {
              id
            }
          }
          occupiedByToday {
            id
            checkOutTime
            account {
              id
            }
          }
        }
        images(limit: 0) {
          id
          url
        }
        internalRef
        nbrBathrooms
        shortInternalRef
        squareFootage
      }
      scheduledStartAt
      status
      subjectType
      building {
        id
        shortInternalRef
      }
      completedAt
      createdAt
      notes
      permissionToEnter
      taskFeedItemConnections {
        lastViewedAt
        unreadCount
      }
      nextRecurrenceTask {
        id
      }
      previousRecurrenceTask {
        id
      }
      tags {
        id
        name
        tagType
      }
      taskContext {
        id
        name
        taskCategory {
          id
          name
        }
        taskLocation {
          id
          name
        }
      }
      cleaningRequest {
        id
        category
        endAt
        startAt
        company {
          id
          city {
            id
          }
          name
        }
        cleaning {
          id
          actualStart
          actualEnd
          noteFromAdmin
          noteFromCompany
        }
      }
      securityDepositItems {
        id
        name
        notes
        attachments {
          attachableId
          attachableType
          id
          resourceType
          url
        }
      }
      subscribers {
        id
      }
      structuredMetadata {
        dateToComplete
        earliestStartTime
        googleFormUrl
        timeGuestReportedInUnit
        securityDepositReason
        securityDepositAmount
        dropOffLocation
        dropOffTime
        pickUpTime
        pickUpQuantity
        intraStayTime
        ota
        reasonForRelocation
        reasonForBan
        reasonForEviction
        reasonForEjection
        guestAlreadyAware
        guestReceivedPreviousWarning
        securityOnsite
        evictionAt
        guestUrgency
        preferredRelocationUnit {
          id
          shortInternalRef
        }
        fallbackRelocationUnit {
          id
          shortInternalRef
        }
        pickUpTask {
          id
        }
        deliveryTask {
          id
        }
        sopExceptionNotes
        requestedAmenities {
          barSoap
          bathMat
          blanket
          bodyLotion
          coffeeCreamer
          coffeeFilter
          coffeePacket
          conditioner
          dishwasherPod
          facialTissue
          garbageBagLarge
          garbageBagSmall
          laundryDetergentPod
          oliveOil
          shampoo
          sheetSet
          sugarRegular
          sugarZing
          teaBlack
          teaGreen
          teaMint
          toiletPaper
          towelBath
          towelFacial
          towelHand
          towelPaper
        }
      }
      qaId
      internalHold {
        holdType
        startAt
        endAt
      }
      tomTaskRecurrenceSetting {
        id
        daysOfWeek
        intervalAmt
        intervalUnit
        threadId
      }
      earliestStartAt
      latestEndAt
      startedAt
    }
  }
`;

export interface UseGetTaskQueryOptions {
  pollInterval?: number;
  variables: GetTaskVariables;
  skip?: boolean;
}
export const useGetTaskQuery = ({
  pollInterval,
  variables,
  skip = false,
}: UseGetTaskQueryOptions) =>
  useQuery<GetTaskResponse, GetTaskVariables>(getTask, {
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    pollInterval,
    skip,
    variables,
  });

export const useRefetchableGetTaskQuery = ({
  pollInterval,
  variables,
  skip = false,
}: UseGetTaskQueryOptions) =>
  useRefetchableQuery<GetTaskResponse, GetTaskVariables>(getTask, {
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    pollInterval,
    skip,
    variables,
  });
